import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { FetchOrdersBatchPayload } from '~/actions/orders-batch.actions';
import OrdersBatch, {
  BatchEntry,
  BatchEntryStatus,
  BatchOrder,
  BatchShipment,
  BatchSteps,
  BatchTransaction,
} from '~/types/OrdersBatch';
import { getLabelUrl } from '~/types/Transaction';
import { listToMapById, listToMapByObjectId } from '~/utils/list';

type ByBatchEntryId = HashMap<number, string[]>;

export type OrdersBatchState = {
  readonly data: Maybe<Partial<OrdersBatch>>;
  readonly orders: {
    data: HashMap<string, BatchOrder>;
    allObjectIds: readonly string[];
  };
  readonly batchEntries: {
    data: HashMap<number, BatchEntry>;
    byOrderObjectId: HashMap<string, number[]>;
  };
  readonly shipments: {
    data: HashMap<number, BatchShipment>;
    byBatchEntryId: ByBatchEntryId;
  };
  readonly transactions: {
    data: HashMap<string, BatchTransaction>;
    byBatchEntryId: ByBatchEntryId;
  };
  readonly wasOrdersFetchTriggered: boolean;
  readonly currentStep: BatchSteps;
  readonly excludedOrdersIds: readonly string[];
  readonly isSavingPackageInformation: boolean;
  readonly isLoadingPackingSlips: boolean;
  readonly packingSlipUrl: Maybe<string>;
  readonly isLoadingMergedLabels: boolean;
  readonly mergedLabelsUrl: Maybe<string>;
  readonly selectedServiceLevelForAllOrders: Maybe<string>;
  readonly hasPollingTimedOut: boolean;
  readonly isCreatingTransactions: boolean;
  readonly inlineEditModalOpen: boolean;
  readonly inlineEditOrderId: string;
  readonly inlineEditModalShowSlideContent: boolean;
  readonly isFetchingUserBatchInput: boolean;
  readonly isLoadingBatch: boolean;
  readonly error: Maybe<string>;
};

export const initialState: OrdersBatchState = {
  currentStep: BatchSteps.NONE,
  data: null,
  orders: {
    data: {},
    allObjectIds: [],
  },
  batchEntries: {
    data: {},
    byOrderObjectId: {},
  },
  shipments: {
    data: {},
    byBatchEntryId: {},
  },
  transactions: {
    data: {},
    byBatchEntryId: {},
  },
  excludedOrdersIds: [],
  wasOrdersFetchTriggered: false,
  isSavingPackageInformation: false,
  // Add second step post loading
  isLoadingPackingSlips: false,
  packingSlipUrl: null,
  isLoadingMergedLabels: false,
  mergedLabelsUrl: null,
  selectedServiceLevelForAllOrders: null,
  hasPollingTimedOut: false,
  isCreatingTransactions: false,
  inlineEditModalOpen: false,
  inlineEditOrderId: '',
  inlineEditModalShowSlideContent: false,
  isFetchingUserBatchInput: false,
  isLoadingBatch: false,
  error: null,
};

function reducer(state: OrdersBatchState = initialState, action: ActionTypes) {
  switch (action.type) {
    case ActionKeys.BATCH_CREATE:
    case ActionKeys.ORDERS_BATCH_CREATE_AND_BUY:
      return {
        ...state,
        isLoadingBatch: true,
      };
    case ActionKeys.ORDERS_BATCH_FETCH:
      // TODO: Set some loading state for said batch
      return {
        ...state,
        batchId: action.batchId,
      };
    case ActionKeys.BATCH_CREATE_SUCCESS:
    case ActionKeys.ORDERS_BATCH_FETCH_SUCCESS:
      const { batch } = action.payload as FetchOrdersBatchPayload;
      const excludedOrdersIds = batch.batchEntries.reduce(
        (excludedOrders: string[], batchEntry) =>
          batchEntry.objectStatus !== BatchEntryStatus.EXCLUDED_ORDER
            ? excludedOrders
            : [...excludedOrders, batchEntry.orderObjectId],
        [],
      );

      const batchEntryIdsByOrderObjectId = batch.batchEntries.reduce<Record<string, number[]>>(
        (acc, batchEntry) => {
          acc[batchEntry.orderObjectId] = acc[batchEntry.orderObjectId]
            ? [...acc[batchEntry.orderObjectId], batchEntry.id]
            : [batchEntry.id];
          return acc;
        },
        {},
      );

      const shipmentObjectIdsByBatchEntryId = batch.shipments.reduce<Record<string, string[]>>(
        (acc, shipment) => {
          acc[shipment.batchEntryId] = acc[shipment.batchEntryId]
            ? [...acc[shipment.batchEntryId], shipment.objectId]
            : [shipment.objectId];
          return acc;
        },
        {},
      );

      const transactionObjectIdsByBatchEntryId = batch.transactions.reduce<
        Record<string, string[]>
      >((acc, transaction) => {
        acc[transaction.batchEntryId] = acc[transaction.batchEntryId]
          ? [...acc[transaction.batchEntryId], transaction.objectId]
          : [transaction.objectId];
        return acc;
      }, {});

      return {
        ...state,
        data: {
          id: batch.id,
          objectCreated: batch.objectCreated,
          objectUpdated: batch.objectUpdated,
          orderGroupStatus: batch.orderGroupStatus,
          shipmentGroupStatus: batch.shipmentGroupStatus,
          transactionGroupStatus: batch.transactionGroupStatus,
          returnShipmentGroupStatus: batch.shipmentGroupStatus,
          returnTransactionGroupStatus: batch.returnTransactionGroupStatus,
          remainingLabels: state.data?.remainingLabels,
          labelLimitRenewalDate: state.data?.labelLimitRenewalDate,
        },
        orders: {
          data: listToMapByObjectId(batch.orders),
          allObjectIds: batch.orders.map((batchOrder) => batchOrder.objectId),
        },
        batchEntries: {
          data: listToMapById(batch.batchEntries),
          byOrderObjectId: batchEntryIdsByOrderObjectId,
        },
        shipments: {
          data: listToMapByObjectId(batch.shipments),
          byBatchEntryId: shipmentObjectIdsByBatchEntryId,
        },
        transactions: {
          data: listToMapByObjectId(batch.transactions),
          byBatchEntryId: transactionObjectIdsByBatchEntryId,
        },
        // Keep currently excluded Order Ids to avoid displaying already excluded ones when doing changes
        excludedOrdersIds: [...state.excludedOrdersIds, ...excludedOrdersIds],
        isLoadingBatch: false,
      };
    case ActionKeys.BATCH_CREATE_ERROR:
      return {
        ...state,
        error: action.payload.error || null,
        isLoadingBatch: false,
      };
    case ActionKeys.ORDERS_BATCH_FETCH_ERROR:
      // TODO: Set some error state for said batch
      return state;
    case ActionKeys.ORDERS_BATCH_STEP_CHANGE:
      return {
        ...state,
        currentStep: action.payload.step,
      };
    case ActionKeys.ORDERS_BATCH_REMOVE_ORDER:
      const orderIdToExclude = action.payload.orderId;
      const excludedOrderIdIndex = state.excludedOrdersIds.findIndex(
        (id) => id === orderIdToExclude,
      );

      return {
        ...state,
        excludedOrdersIds:
          excludedOrderIdIndex === -1
            ? [...state.excludedOrdersIds, action.payload.orderId]
            : state.excludedOrdersIds,
      };
    case ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION:
      // Resets state after the package information is saved, this cleans
      // the state when the user re-sends the batch information
      return {
        ...state,
        batchEntries: { data: {}, byOrderObjectId: {} },
        shipments: { data: {}, byBatchEntryId: {} },
        isSavingPackageInformation: true,
      };
    case ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION_SUCCESS:
    case ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION_ERROR:
      return {
        ...state,
        isSavingPackageInformation: false,
      };
    case ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE:
      return {
        ...state,
        isSavingPackageInformation: true,
      };
    case ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE_SUCCESS:
      return {
        ...state,
        batchEntries: {
          data: {
            ...state.batchEntries.data,
            [action.batchEntryId]: {
              ...state.batchEntries.data[action.batchEntryId],
              objectStatus: BatchEntryStatus.PROCESSING_SHIPMENT,
            },
          },
          byOrderObjectId: {
            ...state.batchEntries.byOrderObjectId,
          },
        },
        isSavingPackageInformation: false,
      };
    case ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE_ERROR:
      return {
        ...state,
        isSavingPackageInformation: false,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS:
      return {
        ...state,
        isLoadingPackingSlips: true,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_SUCCESS:
      return {
        ...state,
        isLoadingPackingSlips: false,
        packingSlipUrl: getLabelUrl(action.payload.slipUrl),
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_ERROR:
      return {
        ...state,
        isLoadingPackingSlips: false,
        packingSlipUrl: null,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS:
      return {
        ...state,
        isLoadingMergedLabels: true,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_SUCCESS:
      return {
        ...state,
        isLoadingMergedLabels: false,
        mergedLabelsUrl: getLabelUrl(action.payload.labelsUrl),
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_ERROR:
      return {
        ...state,
        isLoadingMergedLabels: false,
        mergedLabelsUrl: null,
      };
    case ActionKeys.ORDERS_BATCH_CHANGE_ALL_RATES:
      return {
        ...state,
        selectedServiceLevelForAllOrders: action.payload.serviceLevelToken,
      };
    case ActionKeys.ORDERS_BATCH_POLL_START:
      return {
        ...state,
        hasPollingTimedOut: false,
      };
    case ActionKeys.ORDERS_BATCH_POLL_TIMEOUT:
      return {
        ...state,
        hasPollingTimedOut: true,
      };
    case ActionKeys.ORDERS_BATCH_CLEAN_UP:
      return {
        ...initialState,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS:
      return {
        ...state,
        isCreatingTransactions: true,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR:
      return {
        ...state,
        isCreatingTransactions: false,
      };
    case ActionKeys.ORDERS_BATCH_OPEN_INLINE_EDIT_MODAL:
      return {
        ...state,
        inlineEditModalOpen: true,
        inlineEditOrderId: action.orderId,
      };
    case ActionKeys.ORDERS_BATCH_CLOSE_INLINE_EDIT_MODAL:
      return {
        ...state,
        inlineEditModalOpen: false,
        inlineEditOrderId: '',
      };
    case ActionKeys.ORDERS_BATCH_SLIDE_INLINE_EDIT_MODAL:
      return {
        ...state,
        inlineEditModalShowSlideContent: !state.inlineEditModalShowSlideContent,
      };
    case ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT:
      return {
        ...state,
        isFetchingUserBatchInput: true,
      };
    case ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT_SUCCESS:
      return {
        ...state,
        batchEntries: {
          data: {
            ...state.batchEntries.data,
            [action.batchEntryId]: {
              ...state.batchEntries.data[action.batchEntryId],
              userInput: action.payload.userBatchInput,
            },
          },
          byOrderObjectId: {
            ...state.batchEntries.byOrderObjectId,
          },
        },
        isFetchingUserBatchInput: false,
      };
    case ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT_ERROR:
      return {
        ...state,
        isFetchingUserBatchInput: false,
      };
    case ActionKeys.ORDERS_BATCH_ORDERS_FETCH_TRIGGERED:
      return {
        ...state,
        wasOrdersFetchTriggered: true,
      };
    case ActionKeys.ORDERS_BATCH_TRANSACTION_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          remainingLabels: action.payload.remainingLabels,
          labelLimitRenewalDate: action.payload.labelRenewalDate,
        },
      };
    default:
      return state;
  }
}

export default reducer;
